import * as vp from 'vopay';
import { FunctionComponent, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { useLayout } from '../../../../providers/Layout.provider';
import { useToken } from '../../../../providers/Token.provider';
import { usePaylink } from '../../../../providers/Paylink.provider';
import styles from './BankConnectionTermsAndConditions.module.scss';
import TermsAndConditionsText from './TermsAndConditionsText';
import usePostAcceptAgreement from '../../../../hooks/useApi/usePostAcceptAgreement';
import termsOfServiceText from './TermsOfServiceNov2023.html?raw';
import termsOfServiceTextFR from './TermsOfServiceMar2023FR.html?raw';
import duckCreekPrivacyPolicy from './DuckCreekPrivacyPolicy.html?raw';
import duckCreekTOS from './DuckCreekTOS.html?raw';
import AGMIPrivacyPolicy from './AGMIPrivacyPolicy.html?raw';
import AGMITOS from './AGMITOS.html?raw';

import privacyPolicyText from './PrivacyPolicyNov2023.html?raw';
import privacyPolicyTextFR from './PrivacyPolicyNov2023FR.html?raw';
import useStorage from '@/hooks/useStorage';

const { Checkbox, Box, Button, Modal, Accordion, Title, Icon } = vp;

type BankConnectionTermsAndConditionseProps = {
  showTermsAndConditions: boolean;
  identificationNumber?: string;
  accessToken?: string;
  setShowTermsAndConditions: () => void;
  onNext: () => void;
};

type FC = FunctionComponent<BankConnectionTermsAndConditionseProps>;

const BankConnectionTermsAndConditions: FC = (props: BankConnectionTermsAndConditionseProps) => {
  const { showTermsAndConditions, setShowTermsAndConditions, onNext } = props;
  const { t, i18n } = useTranslation('translations', { keyPrefix: 'BankConnection' });
  const { token } = useToken();
  const { color } = useLayout();
  const { paylink } = usePaylink();
  const [, , acceptAgreement] = usePostAcceptAgreement({});
  const [openAccordion, setOpenAccordion] = useState('');
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);

  const [termsOfServiceAccepted, setTermsOfServiceAccepted] = useState(false);
  const [privacyPolicyAccepted, setPrivacyPolicyAccepted] = useState(false);
  const [preAuthorizedDebitAgreementAccepted, setPreAuthorizedDebitAgreementAccepted] = useState(
    !token?.RequireDebitAuthorityAgreement,
  ); // If the debit authority agreement is not required, then it is accepted by default

  const [termsOfServiceViewed, setTermsOfServiceViewed] = useState(false);
  const [privacyPolicyViewed, setPrivacyPolicyViewed] = useState(false);
  const [preAuthorizedDebitAgreementViewed, setPreAuthorizedDebitAgreementViewed] = useState(false);

  const toggleAccordion = (accordionId: string) => {
    if (openAccordion === 'privacyPolicy' && accordionId !== 'privacyPolicy' && !privacyPolicyAccepted) {
      setPrivacyPolicyViewed(true);
    }

    if (
      openAccordion === 'preAuthorizedDebitAgreement' &&
      accordionId !== 'preAuthorizedDebitAgreement' &&
      !preAuthorizedDebitAgreementAccepted
    ) {
      setPreAuthorizedDebitAgreementViewed(true);
    }

    if (openAccordion === 'termsOfService' && accordionId !== 'termsOfService' && !termsOfServiceAccepted) {
      setTermsOfServiceViewed(true);
    }

    if (openAccordion === accordionId) {
      setOpenAccordion('');
    } else {
      setOpenAccordion(accordionId);
    }
  };

  const handleAcceptAll = () => {
    if (termsOfServiceAccepted && privacyPolicyAccepted && preAuthorizedDebitAgreementAccepted) {
      setTermsOfServiceAccepted(false);
      setPrivacyPolicyAccepted(false);
      setPreAuthorizedDebitAgreementAccepted(false);

      setTermsOfServiceViewed(true);
      setPrivacyPolicyViewed(true);
      setPreAuthorizedDebitAgreementViewed(true);
    } else {
      setTermsOfServiceViewed(false);
      setPrivacyPolicyViewed(false);
      setPreAuthorizedDebitAgreementViewed(false);

      setTermsOfServiceAccepted(true);
      setPrivacyPolicyAccepted(true);
      setPreAuthorizedDebitAgreementAccepted(true);
    }
  };

  useEffect(() => {
    setOpenAccordion('termsOfService');
  }, []);

  useEffect(() => {
    if (termsOfServiceAccepted && privacyPolicyAccepted && preAuthorizedDebitAgreementAccepted) {
      setIsButtonEnabled(true);
    } else {
      setIsButtonEnabled(false);
    }
  }, [termsOfServiceAccepted, privacyPolicyAccepted, preAuthorizedDebitAgreementAccepted]);

  const storage = useStorage();

  const jwt = storage.JWT.getJWT();

  const accountInfo = useMemo(() => {
    if (!jwt) return null;

    return parseJWT(jwt);
  }, [jwt]);

  function parseJWT(token: string) {
    try {
      // Split the token into its three parts
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid JWT format');
      }

      // Decode header and payload (parts[0] and parts[1])
      const header = JSON.parse(atob(parts[0]));
      const payload = JSON.parse(atob(parts[1]));

      return {
        header,
        payload,
        signature: parts[2], // Still base64url encoded
      };
    } catch (error) {
      console.error('Failed to parse JWT:', error);
      return null;
    }
  }

  // TODO: THIS IS JUST A STUPID QUICK FIX!!! DON'T COPY!!!
  const TOSHTML = useMemo(() => {
    if (!accountInfo || !accountInfo.payload.AccountID) {
      return i18n.language === 'en' ? termsOfServiceText : termsOfServiceTextFR;
    }

    if (accountInfo.payload.AccountID === 'drakeinsurance') {
      return duckCreekTOS;
    }

    if (accountInfo.payload.AccountID === 'agmiinsurance') {
      return AGMITOS;
    }

    return i18n.language === 'en' ? termsOfServiceText : termsOfServiceTextFR;
  }, [accountInfo, t]);

  // TODO: THIS IS JUST A STUPID QUICK FIX!!! DON'T COPY!!!
  const privacyPolicyHtml = useMemo(() => {
    if (!accountInfo || !accountInfo.payload.AccountID) {
      return i18n.language === 'en' ? privacyPolicyText : privacyPolicyTextFR;
    }

    if (accountInfo.payload.AccountID === 'drakeinsurance') {
      return duckCreekPrivacyPolicy;
    }

    if (accountInfo.payload.AccountID === 'agmiinsurance') {
      return AGMIPrivacyPolicy;
    }

    return i18n.language === 'en' ? privacyPolicyText : privacyPolicyTextFR;
  }, [accountInfo, t]);

  const modalContent = (
    <Modal isVisible={showTermsAndConditions} onBgClick={setShowTermsAndConditions} size="sm">
      <div className={styles.modalContainer}>
        <div className={styles.titleContainer}>
          <Title title={t('termsAndConditions') || ''} textSize="30px" bold center />
          <Icon
            icon="cross"
            size="sm"
            onClick={setShowTermsAndConditions}
            color={token?.DarkMode ? 'primary-light' : 'primary-dark'}
          />
        </div>
        <Accordion
          title={t('termsOfService') || ''}
          fontSize="16px"
          titleColor={!termsOfServiceViewed ? '' : 'red'}
          isArrowClickable
          variant="normal"
          iconSize="sm"
          iconColor={token?.DarkMode ? 'primary-light' : 'primary-dark'}
          open={openAccordion === 'termsOfService'}
          onClick={() => toggleAccordion('termsOfService')}
          body={
            <TermsAndConditionsText
              // htmlContent={i18n.language === 'en' ? termsOfServiceText : termsOfServiceTextFR}
              htmlContent={TOSHTML}
              title={t('termsOfService') || ''}
              accepted={termsOfServiceAccepted}
              onChange={() => {
                setTermsOfServiceViewed(false);
                setTermsOfServiceAccepted(!termsOfServiceAccepted);
              }}
            />
          }
        />
        <Accordion
          title={t('privacyPolicy') || ''}
          fontSize="16px"
          titleColor={!privacyPolicyViewed ? '' : 'red'}
          isArrowClickable
          variant="normal"
          iconSize="sm"
          iconColor={token?.DarkMode ? 'primary-light' : 'primary-dark'}
          open={openAccordion === 'privacyPolicy'}
          onClick={() => toggleAccordion('privacyPolicy')}
          body={
            <TermsAndConditionsText
              // htmlContent={i18n.language === 'en' ? privacyPolicyText : privacyPolicyTextFR}
              htmlContent={privacyPolicyHtml}
              title={t('privacyPolicy') || ''}
              accepted={privacyPolicyAccepted}
              onChange={() => {
                setPrivacyPolicyViewed(false);
                setPrivacyPolicyAccepted(!privacyPolicyAccepted);
              }}
            />
          }
        />
        {token?.RequireDebitAuthorityAgreement && paylink?.PaymentType === 'fund' && (
          <Accordion
            title={t('debitAgreement') || ''}
            fontSize="16px"
            titleColor={preAuthorizedDebitAgreementViewed ? 'red' : ''}
            isArrowClickable
            variant="normal"
            iconSize="sm"
            iconColor={token?.DarkMode ? 'primary-light' : 'primary-dark'}
            open={openAccordion === 'preAuthorizedDebitAgreement'}
            onClick={() => toggleAccordion('preAuthorizedDebitAgreement')}
            body={
              <TermsAndConditionsText
                pdfUrl={token.AgreementURL}
                title={t('debitAgreement') || ''}
                accepted={preAuthorizedDebitAgreementAccepted}
                onChange={() => {
                  setPreAuthorizedDebitAgreementViewed(false);
                  setPreAuthorizedDebitAgreementAccepted(!preAuthorizedDebitAgreementAccepted);
                }}
              />
            }
          />
        )}
        <div className={styles.acceptCheckBox}>
          <Box flx="row" pb="sm" height="auto" ai="center">
            <Checkbox
              name="accept-all-terms-and-conditions"
              variant="paylink"
              value={isButtonEnabled}
              hasNoPadding
              onBlur={() => {}}
              onChange={() => {
                handleAcceptAll();
              }}
            />
            <Box ps="sm">{t('acceptToS')}</Box>
          </Box>
        </div>
        <Button
          id="connect-bank"
          label={t('continueButton') || ''}
          background={!isButtonEnabled ? '#C4C4C4' : color}
          textColor={!isButtonEnabled ? '#949494' : vp.getContrastColor(color)}
          width="100%"
          height="48px"
          fontUnit="px"
          textSize={16}
          round
          onClick={() => {
            if (isButtonEnabled) {
              acceptAgreement({
                LegalDocumentType: 'terms-of-service',
              });

              acceptAgreement({
                LegalDocumentType: 'debit-authority-agreement',
              });

              onNext();
            }
          }}
        />
      </div>
    </Modal>
  );

  // disabling rules for this line because #modal-root is hardcoded outside of the app.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return createPortal(modalContent, document.getElementById('modal-root')!);
};

export default BankConnectionTermsAndConditions;
